import React, { useEffect } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { DetailedPlayer } from '../components/room/types';
import {
  Avatar,
  Stack,
  Table,
  TableBody,
  TableCell,
  tableCellClasses,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material';
import { resizeImage } from '../api/cloudinary';
import { Mode } from '@par/shared/common/types';

type PropTypes = {
  closeMmrResults: () => void;
  detailedPlayers?: Record<string, DetailedPlayer>;
  mode?: Mode;
  openMmrResults: boolean;
  seedDetails?: {
    submittedBy: string;
    submittedByRating: number;
  };
};

export const MmrResults = ({
  closeMmrResults,
  detailedPlayers,
  mode,
  openMmrResults,
  seedDetails,
}: PropTypes) => {
  const [sortedByDiff, setSortedByDiff] = React.useState<DetailedPlayer[]>();

  useEffect(() => {
    if (!detailedPlayers) return;
    setSortedByDiff(
      Object.values(detailedPlayers).sort(
        (a, b) => (b.rankedRatingDiff || 0) - (a.rankedRatingDiff || 0)
      )
    );
  }, [detailedPlayers]);

  const theme = useTheme();

  const getMmrRatingDiff = (player: DetailedPlayer) => {
    if (player.rankedRatingDiff !== undefined) return player.rankedRatingDiff;
    return 0;
  };

  const formatMmrRatingDiff = (player: DetailedPlayer) => {
    if (player.rankedRatingDiff !== undefined)
      return `${player.rankedRatingDiff > 0 ? '+' : ''}${Math.round(
        player.rankedRatingDiff
      )}`;
  };

  const formatMmrRating = (player: DetailedPlayer) => {
    if (player.rankedMatchGamesPlayed === undefined) {
      if (seedDetails?.submittedBy === player.profileId) {
        return (
          <Stack>
            <Typography>
              (seeded at {seedDetails.submittedByRating} MMR)
            </Typography>
          </Stack>
        );
      } else {
        return '--';
      }
    }
    if (
      (player.rankedMatchGamesPlayed || 0) +
        (player.rankedSitAndGoGamesPlayed || 0) <
      10
    ) {
      if (seedDetails?.submittedBy === player.profileId) {
        return (
          <Stack>
            <Typography>calibrating</Typography>
            <Typography>
              (seeded at {seedDetails.submittedByRating} MMR)
            </Typography>
          </Stack>
        );
      }
      return 'calibrating';
    }
    if (
      player.rankedRating !== undefined &&
      player.rankedRatingDiff !== undefined
    ) {
      if (seedDetails?.submittedBy === player.profileId) {
        return (
          <Stack>
            <Typography>
              {Math.floor(player.rankedRating + player.rankedRatingDiff)}
            </Typography>
            <Typography>
              (seeded at {seedDetails.submittedByRating} MMR)
            </Typography>
          </Stack>
        );
      }
      return Math.floor(player.rankedRating + player.rankedRatingDiff);
    }
    return 'error';
  };

  return (
    <>
      {detailedPlayers && mode ? (
        <Dialog
          open={openMmrResults}
          onClose={closeMmrResults}
          fullWidth={true}
          maxWidth={'sm'}
          sx={{
            '& .paper': {
              borderStyle: 'solid',
              borderColor: theme.palette.ranked.main,
            },
          }}
        >
          <DialogTitle>
            <Typography variant="h4" textAlign={'center'} letterSpacing={3}>
              MMR Results
            </Typography>
          </DialogTitle>
          <DialogContent>
            <TableContainer>
              <Table
                size="small"
                sx={{
                  [`& .${tableCellClasses.root}`]: {
                    // fontSize: 20,
                    borderBottom: 'none',
                  },
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <Typography fontWeight={600}>Player</Typography>
                    </TableCell>
                    <TableCell width={'25%'} sx={{ textAlign: 'center' }}>
                      <Typography fontWeight={600}>Points +/-</Typography>
                    </TableCell>
                    <TableCell width={'35%'} sx={{ textAlign: 'center' }}>
                      <Typography
                        fontWeight={600}
                        sx={{ color: theme.palette.ranked.main }}
                      >
                        New MMR
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedByDiff?.map((p) => (
                    <TableRow key={p.profileId}>
                      <TableCell>
                        <Stack direction={'row'} spacing={2} width={'250px'}>
                          <Avatar
                            src={resizeImage(p.avatarPublicId, 50, 50)}
                            variant="rounded"
                            sx={{
                              height: 40,
                              width: 40,
                            }}
                          ></Avatar>
                          <Typography
                            sx={{
                              lineHeight: '40px',
                              textOverflow: 'ellipsis',
                              overflow: 'hidden',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {p.username}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Typography
                          textAlign={'center'}
                          sx={{
                            color:
                              getMmrRatingDiff(p) >= 0
                                ? 'success.main'
                                : 'error.main',
                          }}
                        >
                          {formatMmrRatingDiff(p)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          textAlign={'center'}
                          fontWeight={600}
                          sx={{ color: theme.palette.ranked.main }}
                        >
                          {formatMmrRating(p)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </DialogContent>
        </Dialog>
      ) : null}
    </>
  );
};
